import React, { useState } from 'react';
import { Check, Loader2, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import { ClaireService } from '../../services/claireService';
import type { ClaireApproval } from '../../lib/claire/types';
import { cn } from '../../utils/cn';

const DONE_LABEL: Partial<Record<ClaireApproval['status'], string>> = {
  running: 'Working on it…',
  approved: 'Done',
  rejected: 'Cancelled',
  expired: 'Expired',
  failed: 'Didn’t go through',
};

/** "Claire wants to…" card. Confirm-level actions are approved by the asker; elevated ones by a Master Admin. */
export const ClaireApprovalCard: React.FC<{ approval: ClaireApproval; isMasterAdmin: boolean; compact?: boolean }> = ({ approval: a, isMasterAdmin, compact }) => {
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canDecide = a.status === 'pending' && (a.level === 'confirm' || isMasterAdmin);
  const elevated = a.level === 'elevated';

  const decide = async (decision: 'approve' | 'reject') => {
    setBusy(decision);
    setError(null);
    try {
      await ClaireService.decideApproval(a.id, decision);
    } catch (e: any) {
      setError(e?.message?.replace(/^.*?:\s*/, '') || 'Could not reach Claire.');
    } finally {
      setBusy(null);
    }
  };

  const Icon = elevated ? ShieldAlert : ShieldCheck;
  return (
    <div
      className={cn(
        'rounded-xl border px-3.5 py-3',
        a.status === 'pending' ? (elevated ? 'border-amber-300 bg-amber-50/70' : 'border-[#e0d3bf] bg-[#fbf7f0]') : 'border-[#ece4d7] bg-white',
        compact ? 'text-[12.5px]' : 'text-[13.5px]',
      )}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', elevated ? 'text-amber-700' : 'text-[#7a5a3a]')} />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9a8a78]">
            {a.status === 'pending' ? (elevated ? 'Needs Master Admin approval' : 'Claire wants to') : a.displayName || 'Action'}
          </p>
          <p className="mt-0.5 text-[#241813] whitespace-pre-wrap break-words">{a.preview}</p>
          {a.status !== 'pending' && (
            <p className={cn('mt-1 text-[12px]', a.status === 'approved' ? 'text-emerald-700' : a.status === 'failed' ? 'text-red-700' : 'text-[#8a7a6a]')}>
              {DONE_LABEL[a.status]}
              {a.decidedByName && a.status !== 'running' ? ` · ${a.decidedByName}` : ''}
              {a.status === 'failed' && a.error ? ` — ${a.error}` : ''}
            </p>
          )}
          {a.status === 'pending' && !canDecide && <p className="mt-1 text-[12px] text-amber-800">A Master Admin will see this in Master Claire.</p>}
          {error && <p className="mt-1 text-[12px] text-red-700">{error}</p>}
        </div>
      </div>
      {canDecide && (
        <div className="mt-2.5 flex gap-2 pl-[26px]">
          <button
            onClick={() => decide('approve')}
            disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#241813] text-white px-3.5 py-1.5 text-[12.5px] font-medium hover:bg-black disabled:opacity-50"
          >
            {busy === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {elevated ? 'Approve' : 'Yes, do it'}
          </button>
          <button
            onClick={() => decide('reject')}
            disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#e0d6c6] bg-white px-3.5 py-1.5 text-[12.5px] text-[#5a4a3a] hover:border-[#b8986e] disabled:opacity-50"
          >
            {busy === 'reject' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
